<template>
  <div>
    <p class="tw:font-bold tw:mb-3">{{ label }}</p>
    <ul class="tw:flex tw:flex-col">
      <template v-for="item in options" :key="item.id || item.group?.id">
        <!-- Individual item (no group) -->
        <li v-if="!item.group" class="tw:flex tw:items-center tw:text-sm">
          <input
            type="checkbox"
            :name="name"
            :value="item.id"
            :id="`${name}_${item.id}`"
            :checked="isSelected(item.id)"
            @change="onSelect(item)"
            class="tw:cursor-pointer"
          />
          <label
            :for="`${name}_${item.id}`"
            class="tw:cursor-pointer tw:py-2 tw:m-0 tw:pl-3 tw:grow tw:flex tw:items-center tw:flex-wrap"
          >
            {{ $t(item.label) }}
          </label>
        </li>

        <!-- Grouped items -->
        <template v-else>
          <!-- Group header -->
          <li class="item tw:flex tw:items-center tw:text-sm tw:mt-2">
            <input
              type="checkbox"
              :name="`${name}_group_${item.group.id}`"
              :id="`${name}_group_${item.group.id}`"
              :checked="isGroupFullySelected(item.options)"
              :indeterminate.prop="isGroupPartiallySelected(item.options)"
              @change="onSelect(item)"
              class="tw:cursor-pointer"
            />

            <label
              :for="`${name}_group_${item.group.id}`"
              class="tw:cursor-pointer tw:py-2 tw:m-0 tw:pl-3 tw:grow tw:flex tw:items-center tw:flex-wrap"
            >
              {{ $t(item.group.label) }}
            </label>
            <button
              @click="toggleGroupExpanded(item.group.id)"
              type="button"
              class="tw:flex tw:items-center tw:relative tw:p-4 tw:hover:cursor-pointer"
            >
              <ChevronRight
                class="tw:absolute tw:size-6"
                :class="isGroupExpanded(item.group.id) ? 'arrow-dropdown-open' : 'arrow-dropdown'"
              />
            </button>
          </li>

          <!-- Group options -->
          <transition-group name="collapse" tag="div">
            <li
              v-show="isGroupExpanded(item.group.id)"
              v-for="option in item.options"
              :key="option.id"
              class="item tw:flex tw:items-center tw:text-sm tw:ml-6"
            >
              <input
                type="checkbox"
                :name="name"
                :value="option.id"
                :id="`${name}_${option.id}`"
                :checked="isSelected(option.id)"
                @change="onSelect(option)"
                class="tw:cursor-pointer"
              />
              <label
                :for="`${name}_${option.id}`"
                class="tw:cursor-pointer tw:py-2 tw:m-0 tw:pl-3 tw:grow tw:flex tw:items-center tw:flex-wrap"
              >
                {{ $t(option.label) }}
              </label>
            </li>
          </transition-group>
        </template>
      </template>
    </ul>
  </div>
</template>

<script setup>
import { ref } from 'vue'

import ChevronRight from '@/components/icons/ChevronRight.svg?component'

const emit = defineEmits(['onSelect'])
const onSelect = newValue => emit('onSelect', newValue)

const props = defineProps({
  name: String,
  label: String,
  options: Array,
  value: Array,
})

const expandedGroups = ref(new Set())

const toggleGroupExpanded = groupId => {
  const newExpanded = new Set(expandedGroups.value)
  if (newExpanded.has(groupId)) {
    newExpanded.delete(groupId)
  } else {
    newExpanded.add(groupId)
  }
  expandedGroups.value = newExpanded
}

const isGroupExpanded = groupId => {
  return expandedGroups.value.has(groupId)
}

const isSelected = itemId => {
  return props.value.some(item => item.id === itemId)
}

const isGroupFullySelected = groupOptions => {
  if (!groupOptions || groupOptions.length === 0) return false
  return groupOptions.every(option => isSelected(option.id))
}

const isGroupPartiallySelected = groupOptions => {
  if (!groupOptions || groupOptions.length === 0) return false
  const selectedCount = groupOptions.filter(option => isSelected(option.id)).length
  return selectedCount > 0 && selectedCount < groupOptions.length
}
</script>

<style scoped>
.item {
  flex-basis: 100%;
  box-sizing: border-box;
}
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
.arrow-dropdown-open {
  top: 50%;
  transform: translateY(-45%) rotate(270deg);
  right: 6px;
  pointer-events: none;
}
</style>
